import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Skeleton,
  Tag,
  TagLabel,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { ColumnType } from "build-ts/enums";
import { KGColumnProto } from "build-ts/knowledege_graph";
import { getKGColumnById, updateKGColumn } from "services/DataModelService";
import { listScheduledJobsByOwnerId, setScheduledJobIsActive } from "services/ScheduleServices";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { useConfigurationContext } from "context/ConfigurationContext";
import showToast from "hooks/useCustomToast";
import { useEditorShellTokens } from "utils/editorShellStyles";
import { TooltipHintIcon } from "components/common/TooltipHintIcon";
import { getTooltip } from "utils/getMessages";
import ColumnSyncJobComponent from "./mkg/ColumnSyncJobComponent";

interface EditKGColumnComponentProps {
  id: string | undefined;
  onClose: () => void;
  onSave?: () => void;
  triggerSubmit?: boolean;
  setTriggerSubmit?: (value: boolean) => void;
  onReloadKnowledgeGraph?: () => void;
  compact?: boolean;
}

const EditKGColumnComponent: React.FC<EditKGColumnComponentProps> = ({
  id,
  onClose,
  onSave,
  triggerSubmit,
  setTriggerSubmit,
  onReloadKnowledgeGraph,
  compact,
}) => {
  const t = useEditorShellTokens(compact ?? false);
  const { modelList, fetchModelList } = useConfigurationContext();
  const [selectedColumn, setSelectedColumn] = useState<KGColumnProto | null>(null);
  const [columnType, setColumnType] = useState<ColumnType>(ColumnType.ATTRIBUTE);
  /** Backend: 1 = low cardinality, 0 = high cardinality */
  const [cardinality, setCardinality] = useState<number>(0);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showMappings, setShowMappings] = useState<boolean>(false);

  const handleKGColumn = async (id: string) => {
    if (!id) {
      setSelectedColumn(null);
      return;
    }
    setIsLoading(true);
    const kgColumn = await getKGColumnById(id, true);
    if (kgColumn) {
      setSelectedColumn(kgColumn);
      setName(kgColumn.header?.name || "");
      setDescription(kgColumn.header?.description || "");
      setColumnType(kgColumn.column_type || ColumnType.ATTRIBUTE);
      setCardinality(kgColumn.cardinality === 1 ? 1 : 0);
    }
    setIsLoading(false);
  };

  const handleSave = async () => {
    if (!selectedColumn) return;
    if (!name.trim()) {
      showToast("Name is required", "", "error");
      return;
    }
    const updatedHeader = { ...selectedColumn.header, name, description };
    const updatedKgColumn: KGColumnProto = {
      ...selectedColumn,
      header: updatedHeader,
      column_type: columnType,
    };

    if (columnType === ColumnType.ATTRIBUTE) {
      updatedKgColumn.cardinality = cardinality === 1 ? 1 : 0;
      delete updatedKgColumn.aggregation;
      delete updatedKgColumn.time_bucket;
    } else if (columnType === ColumnType.MEASURE) {
      // Aggregation not edited in UI; keep existing value from selectedColumn via spread.
      delete updatedKgColumn.cardinality;
      delete updatedKgColumn.time_bucket;
    } else if (columnType === ColumnType.TEMPORAL) {
      delete updatedKgColumn.aggregation;
      delete updatedKgColumn.cardinality;
      // Time bucket not edited in UI; keep existing value from selectedColumn via spread.
    }

    const result = await updateKGColumn(updatedKgColumn, "", [], [], false);
    if (result) {
      showToast("KG Column updated successfully", "", "success");
      // When cardinality flips from low → high, deactivate the sync job since syncing
      // is no longer applicable. Activation when going high → low is left to the user.
      const originalCardinality = selectedColumn.cardinality === 1 ? 1 : 0;
      if (columnType === ColumnType.ATTRIBUTE
          && originalCardinality === 1 && cardinality === 0
          && selectedColumn.header?.id) {
        const jobs = await listScheduledJobsByOwnerId(selectedColumn.header.id);
        const syncJob = jobs?.find(
          (j: ScheduledJobProto) => j.type === ScheduledJobType.COLUMN_SYNC_JOB
        );
        if (syncJob?.header?.id) {
          await setScheduledJobIsActive(syncJob.header.id, false);
        }
      }
      onSave?.();
      onClose();
      onReloadKnowledgeGraph?.();
    }
  };

  useEffect(() => {
    if (id) {
      handleKGColumn(id);
    }
  }, [id]);

  useEffect(() => {
    if (modelList.length === 0) fetchModelList();
  }, []);

  // Map of model id to model name
  const modelNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const h of [...modelList]) {
      if (h?.id) map[h.id] = h.name?.trim() || h.id;
    }
    return map;
  }, [modelList]);

  useEffect(() => {
    if (triggerSubmit) {
      handleSave();
      // Reset the trigger after submitting
      if (setTriggerSubmit) {
        setTriggerSubmit(false);
      }
    }
  }, [triggerSubmit]);

  if (isLoading) {
    return (
      <VStack spacing={t.vStackSpacing} align="stretch" p={t.contentPx}>
        <Skeleton height={t.compact ? "32px" : "40px"} borderRadius="md" />
        <Skeleton height={t.compact ? "80px" : "100px"} borderRadius="md" />
        <Skeleton height={t.compact ? "32px" : "40px"} borderRadius="md" />
        <Skeleton height={t.compact ? "32px" : "40px"} borderRadius="md" />
      </VStack>
    );
  }

  if (!selectedColumn) {
    return (
      <Box p={t.compact ? 6 : 10} textAlign="center">
        <Text color="gray.400" fontSize={t.bodyText}>
          {id ? "Loading column details…" : "No column selected"}
        </Text>
      </Box>
    );
  }

  const hasMappings = selectedColumn.logical_columns && selectedColumn.logical_columns.length > 0;

  const SectionBar = ({ label }: { label: string }) => (
    <Box
      bg="gray.50"
      px={t.sectionPx}
      py={t.sectionPy}
      borderTopWidth="1px"
      borderBottomWidth="1px"
      borderColor="gray.100"
    >
      <Text
        fontWeight="600"
        fontSize={t.sectionTitleSize}
        color="gray.600"
        textAlign="left"
        letterSpacing="normal"
        mb={0}
      >
        {label}
      </Text>
    </Box>
  );

  return (
    <VStack spacing={0} align="stretch">

      {/* Core + type-specific (single Properties section, no duplicate bar) */}
      <SectionBar label="Properties" />
      <VStack
        spacing={t.vStackSpacing}
        align="stretch"
        px={t.contentPx}
        pt={t.contentPt}
        pb={t.contentPb}
        bg="white"
      >
        <FormControl isRequired>
          <FormLabel {...t.label}>Name</FormLabel>
          <Input
            {...t.field}
            size={t.inputSize}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Column name"
          />
        </FormControl>

        <FormControl>
          <FormLabel {...t.label}>Semantics</FormLabel>
          <Textarea
            {...t.field}
            size={t.inputSize}
            rows={t.textareaRows}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what this column represents, how it's used, and any notes…"
            resize="vertical"
          />
        </FormControl>

        {columnType === ColumnType.ATTRIBUTE && (
          <FormControl>
            <HStack mb={1} align="center" spacing={2}>
              <Text {...t.label} mb={0}>
                Cardinality
              </Text>
              <TooltipHintIcon
                tooltipKey="edit_kg_column.cardinality.tooltip"
                size={t.compact ? "sm" : "md"}
              />
            </HStack>
            <HStack align="center" spacing={3} w="fit-content" justify="flex-start">
              <HStack spacing={0} p={0.5} bg="gray.100" borderRadius="md" flexShrink={0}>
                <Button
                  size={t.compact ? "xs" : "sm"}
                  variant={cardinality === 1 ? "solid" : "ghost"}
                  colorScheme="purple"
                  fontWeight="600"
                  borderRadius="md"
                  onClick={() => setCardinality(1)}
                >
                  Low
                </Button>
                <Button
                  size={t.compact ? "xs" : "sm"}
                  variant={cardinality === 0 ? "solid" : "ghost"}
                  colorScheme="purple"
                  fontWeight="600"
                  borderRadius="md"
                  onClick={() => setCardinality(0)}
                >
                  High
                </Button>
              </HStack>
              <Text fontSize={t.bodyText} color="gray.700" lineHeight="short" mb={0} flex={1} minW={0} fontWeight="500">
                {cardinality === 1
                  ? getTooltip("edit_kg_column.cardinality.low_hint")
                  : getTooltip("edit_kg_column.cardinality.high_hint")}
              </Text>
            </HStack>
          </FormControl>
        )}

        {/*
        {columnType === ColumnType.MEASURE && (
          <FormControl>
            <FormLabel {...t.label}>Default Aggregation</FormLabel>
            <Select
              {...t.field}
              size={t.inputSize}
              value={aggregationTypeToJSON(aggregation)}
              onChange={(e) => setAggregation(aggregationTypeFromJSON(e.target.value))}
            >
              <option value={aggregationTypeToJSON(AggregationType.SUM)}>Sum</option>
              <option value={aggregationTypeToJSON(AggregationType.AVERAGE)}>Average</option>
              <option value={aggregationTypeToJSON(AggregationType.COUNT)}>Count</option>
              <option value={aggregationTypeToJSON(AggregationType.UNIQUE_COUNT)}>Unique Count</option>
              <option value={aggregationTypeToJSON(AggregationType.GROWTH)}>Growth</option>
              <option value={aggregationTypeToJSON(AggregationType.MIN)}>Min</option>
              <option value={aggregationTypeToJSON(AggregationType.MAX)}>Max</option>
            </Select>
          </FormControl>
        )}

        {columnType === ColumnType.TEMPORAL && (
          <FormControl>
            <FormLabel {...t.label}>Time Bucket</FormLabel>
            <Select
              {...t.field}
              size={t.inputSize}
              value={timeBucketToJSON(timeBucket)}
              onChange={(e) => setTimeBucket(timeBucketFromJSON(e.target.value))}
            >
              <option value={timeBucketToJSON(TimeBucket.DAILY)}>Daily</option>
              <option value={timeBucketToJSON(TimeBucket.WEEKLY)}>Weekly</option>
              <option value={timeBucketToJSON(TimeBucket.MONTHLY)}>Monthly</option>
              <option value={timeBucketToJSON(TimeBucket.QUARTERLY)}>Quarterly</option>
              <option value={timeBucketToJSON(TimeBucket.YEARLY)}>Yearly</option>
            </Select>
          </FormControl>
        )}
        */}
      </VStack>

      {/* Source columns — collapsible, hidden by default */}
      {hasMappings && (
        <>
          <HStack
            bg="gray.50"
            px={t.sectionPx}
            py={t.sectionPy}
            justify="space-between"
            align="center"
            w="100%"
            cursor="pointer"
            userSelect="none"
            onClick={() => setShowMappings(!showMappings)}
            _hover={{ bg: "gray.100" }}
            borderTopWidth="1px"
            borderBottomWidth="1px"
            borderColor="gray.100"
            mb={t.compact ? 1 : 2}
          >
            <HStack spacing={2} flex="1" minW={0} justify="flex-start">
              <Text
                fontWeight="600"
                fontSize={t.sectionTitleSize}
                color="gray.600"
                textAlign="left"
                letterSpacing="normal"
                mb={0}
              >
                Source columns
              </Text>
              <Box bg="gray.200" borderRadius="full" px={1.5} py={0}>
                <Text fontSize="xs" color="gray.600" mb={0} fontWeight="700">
                  {selectedColumn.logical_columns?.length}
                </Text>
              </Box>
            </HStack>
            <Icon as={showMappings ? FiChevronUp : FiChevronDown} boxSize={t.iconBox} color="gray.400" />
          </HStack>

          <Collapse in={showMappings} animateOpacity>
            <Box px={t.contentPx} pb={t.contentPb} w="100%" textAlign="left">
              <VStack align="stretch" spacing={t.compact ? 2.5 : 3}>
                {selectedColumn.logical_columns?.map((column, index) => {
                  const ownerId = column?.owner_id || "";
                  const modelName = modelNameById[ownerId] || `Model (${ownerId.slice(0, 8)}…)`;
                  const sourceColumn = column?.name?.trim() || "—";
                  const tagSize = t.compact ? "sm" : "md";
                  const labelFontSize = t.compact ? "xs" : "sm";
                  const labelTextProps = {
                    fontSize: labelFontSize,
                    fontWeight: "600" as const,
                    color: "gray.500",
                    mb: 0,
                    lineHeight: "short" as const,
                  };
                  return (
                    <Box
                      key={`${column?.owner_id ?? ""}-${column?.id ?? ""}-${index}`}
                      w="100%"
                      py={t.compact ? 1 : 1.5}
                      borderBottomWidth={
                        index < (selectedColumn.logical_columns?.length ?? 0) - 1 ? "1px" : undefined
                      }
                      borderColor="gray.100"
                    >
                      <VStack align="flex-start" spacing={t.compact ? 2 : 2.5} w="100%">
                        <VStack align="flex-start" spacing={1} w="100%">
                          <Tag
                            size={tagSize}
                            colorScheme="purple"
                            variant="outline"
                            maxW="100%"
                            borderRadius="md"
                            fontWeight="700"
                          >
                            <TagLabel whiteSpace="normal" wordBreak="break-word">
                              {sourceColumn}
                            </TagLabel>
                          </Tag>
                        </VStack>
                        <VStack align="flex-start" spacing={1} w="100%">
                          <Text {...labelTextProps}>From data model</Text>
                          <Tag
                            size={tagSize}
                            colorScheme="purple"
                            variant="outline"
                            maxW="100%"
                            borderRadius="md"
                          >
                            <TagLabel whiteSpace="normal" wordBreak="break-word">
                              {modelName}
                            </TagLabel>
                          </Tag>
                        </VStack>
                      </VStack>
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          </Collapse>
          {/* Column Sync Job — only for low-cardinality attributes */}
          {selectedColumn?.header
            && columnType === ColumnType.ATTRIBUTE
            && cardinality === 1 && (
            <ColumnSyncJobComponent kgColumnHeader={selectedColumn.header} compact={compact} />
          )}
        </>
      )}
    </VStack>
  );
};

export default EditKGColumnComponent;
